import { useEffect, lazy, Suspense } from "react";
import { Switch, Route, Router as WouterRouter, useLocation, Redirect } from "wouter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as SonnerToaster } from "sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { WhatsAppFAB } from "@/components/WhatsAppFAB";
import { CartDrawer } from "@/components/CartDrawer";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { ScrollToTop } from "@/components/ScrollToTop";
import type { UserRole } from "@/lib/supabase";
import { ROUTE_META, fallbackTitle } from "@/constants/seo";

// Pages (Lazy Loaded)
const Landing        = lazy(() => import("@/pages/landing"));
const AuthPage       = lazy(() => import("@/pages/auth"));
const ContactPage    = lazy(() => import("@/pages/contact"));
const PricingPage    = lazy(() => import("@/pages/pricing"));
const CheckoutPage   = lazy(() => import("@/pages/checkout"));
const ResourcesPage  = lazy(() => import("@/pages/resources"));
const StudentsPage   = lazy(() => import("@/pages/students"));
const TeachersPage   = lazy(() => import("@/pages/teachers"));
const SchoolsPage    = lazy(() => import("@/pages/schools"));
const TutorsPage     = lazy(() => import("@/pages/tutors"));
const CorePage       = lazy(() => import("@/pages/core"));
const NotesPage      = lazy(() => import("@/pages/students/notes"));
const PastPapersPage = lazy(() => import("@/pages/students/past-papers"));
const SubjectsPage   = lazy(() => import("@/pages/subjects"));
const StudyToolsPage = lazy(() => import("@/pages/students/study-tools"));
const LessonPlansPage       = lazy(() => import("@/pages/teachers/lesson-plans"));
const RubricsPage           = lazy(() => import("@/pages/teachers/rubrics"));
const SlideDecksPage        = lazy(() => import("@/pages/teachers/slide-decks"));
const IaLabSupportPage      = lazy(() => import("@/pages/teachers/ia-lab-support"));
const ProfDevPage           = lazy(() => import("@/pages/teachers/professional-development"));
const EnterprisePage        = lazy(() => import("@/pages/schools/enterprise"));
const CoordinatorPage       = lazy(() => import("@/pages/schools/coordinator"));
const AnalyticsPage         = lazy(() => import("@/pages/schools/analytics"));
const AdminDashboard   = lazy(() => import("@/pages/admin-portal/index"));
const AdminLayout      = lazy(() => import("@/pages/admin-portal/AdminLayout"));
const AdminNotifications = lazy(() => import("@/pages/admin-portal/AdminNotifications"));
const AdminAnalytics   = lazy(() => import("@/pages/admin-portal/AdminAnalytics"));
const AdminResources   = lazy(() => import("@/pages/admin-portal/AdminResources"));
const AdminUploadHub   = lazy(() => import("@/pages/admin-portal/AdminUploadHub"));
const AdminStudents    = lazy(() => import("@/pages/admin-portal/AdminStudents"));
const AdminTeachers    = lazy(() => import("@/pages/admin-portal/AdminTeachers"));
const AdminSchools     = lazy(() => import("@/pages/admin-portal/AdminSchools"));
const AdminSubjects    = lazy(() => import("@/pages/admin-portal/AdminSubjects"));
const AdminRevenue     = lazy(() => import("@/pages/admin-portal/AdminRevenue"));
const AdminSubscriptions = lazy(() => import("@/pages/admin-portal/AdminSubscriptions"));
const AdminSettings     = lazy(() => import("@/pages/admin-portal/AdminSettings"));
const AdminLogs         = lazy(() => import("@/pages/admin-portal/AdminLogs"));
const AdminAssignments  = lazy(() => import("@/pages/admin-portal/AdminAssignments"));
const AdminTestimonials = lazy(() => import("@/pages/admin-portal/AdminTestimonials"));
const AdminTutorApplications = lazy(() => import("@/pages/admin-portal/AdminTutorApplications"));
const BecomeTutorPage = lazy(() => import("@/pages/become-a-tutor"));
const TeacherDashboard = lazy(() => import("@/pages/teacher"));
const StudentDashboard = lazy(() => import("@/pages/student"));
const AboutPage       = lazy(() => import("@/pages/about"));
const BlogPage        = lazy(() => import("@/pages/blog"));
const BlogPostPage    = lazy(() => import("@/pages/blog-post"));
const AdminBlog       = lazy(() => import("@/pages/admin-portal/AdminBlog"));
const AdminLessons    = lazy(() => import("@/pages/admin-portal/AdminLessons"));
const CareersPage     = lazy(() => import("@/pages/careers"));
const PrivacyPage     = lazy(() => import("@/pages/privacy"));
const TermsPage       = lazy(() => import("@/pages/terms"));
const IAPage          = lazy(() => import("@/pages/ia"));
const TestimonialsPage = lazy(() => import("@/pages/testimonials"));
const NotFound        = lazy(() => import("@/pages/not-found"));

const StudentLayout      = lazy(() => import("@/pages/student-portal/StudentLayout"));
const StudentProgress    = lazy(() => import("@/pages/student-portal/StudentProgress"));
const StudentResources   = lazy(() => import("@/pages/student-portal/StudentResources"));
const StudentTutors      = lazy(() => import("@/pages/student-portal/StudentTutors"));
const StudentIaTracker   = lazy(() => import("@/pages/student-portal/StudentIaTracker"));
const StudentCountdown   = lazy(() => import("@/pages/student-portal/StudentCountdown"));
const StudentSettings    = lazy(() => import("@/pages/student-portal/StudentSettings"));
const SubmitTestimonial  = lazy(() => import("@/pages/student-portal/SubmitTestimonial"));

const TeacherLayout      = lazy(() => import("@/pages/teacher-portal/TeacherLayout"));
const TeacherClasses     = lazy(() => import("@/pages/teacher-portal/TeacherClasses"));
const TeacherFeedback    = lazy(() => import("@/pages/teacher-portal/TeacherFeedback"));
const TeacherLibrary     = lazy(() => import("@/pages/teacher-portal/TeacherLibrary"));
const TeacherStudents    = lazy(() => import("@/pages/teacher-portal/TeacherStudents"));
const TeacherSettings    = lazy(() => import("@/pages/teacher-portal/TeacherSettings"));
const TeacherSchedule    = lazy(() => import("@/pages/teacher-portal/TeacherSchedule"));

const queryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: false, retry: false } },
});

import { Skeleton } from "@/components/ui/skeleton";

/* ── Dynamic Page Title Handler ────────────────────────────────── */
// Titles/descriptions live in constants/seo.ts, shared with the build-time prerenderer.
function TitleHandler() {
  const [location] = useLocation();

  useEffect(() => {
    const meta = ROUTE_META[location];
    document.title = meta?.title ?? fallbackTitle(location);
    if (meta) {
      document.querySelector('meta[name="description"]')?.setAttribute("content", meta.description);
    }
  }, [location]);

  return null;
}

/* ── Elite Dashboard Skeleton ──────────────────────────────── */
function DashboardSkeleton() {
  return (
    <div className="min-h-screen bg-[#0D1520] flex overflow-hidden">
      {/* Sidebar Skeleton */}
      <div className="hidden lg:flex w-64 xl:w-72 flex-col bg-[#111E2E] border-r border-white/5 p-6 animate-pulse">
        <div className="flex items-center gap-3 mb-12">
          <Skeleton className="w-9 h-9 bg-white/10" />
          <div className="space-y-2">
            <Skeleton className="h-4 w-24 bg-white/10" />
            <Skeleton className="h-2 w-16 bg-white/5" />
          </div>
        </div>
        <div className="space-y-6">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="w-4 h-4 bg-white/10" />
              <Skeleton className="h-3 w-32 bg-white/5" />
            </div>
          ))}
        </div>
      </div>

      {/* Content Area Skeleton */}
      <div className="flex-1 flex flex-col">
        {/* Header Skeleton */}
        <header className="h-20 border-b border-white/5 px-8 flex items-center justify-between">
          <Skeleton className="h-10 w-96 bg-white/5 rounded-2xl" />
          <div className="flex items-center gap-6">
            <Skeleton className="w-5 h-5 rounded-full bg-white/10" />
            <Skeleton className="w-10 h-10 rounded-xl bg-white/10" />
          </div>
        </header>

        {/* Main Body Skeleton */}
        <main className="p-12 space-y-8">
          <div className="flex justify-between items-end">
            <div className="space-y-4">
              <Skeleton className="h-4 w-32 bg-white/10" />
              <Skeleton className="h-12 w-64 bg-white/10" />
            </div>
            <Skeleton className="h-12 w-48 bg-[#F0A500]/10" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Skeleton className="h-32 bg-white/5 rounded-3xl" />
            <Skeleton className="h-32 bg-white/5 rounded-3xl" />
            <Skeleton className="h-32 bg-white/5 rounded-3xl" />
          </div>
          <Skeleton className="h-96 bg-white/5 rounded-3xl w-full" />
        </main>
      </div>
    </div>
  );
}

/* ── Role-based protected route ──────────────────────────────── */
function ProtectedRoute({
  component: Component,
  allowedRoles,
}: {
  component: React.ComponentType;
  allowedRoles?: UserRole[];
}) {
  const { user, profile, loading } = useAuth();
  const [location, navigate] = useLocation();

  /* All navigation must happen in an effect — never during render */
  useEffect(() => {
    if (loading) return;

    if (!user) {
      // Tutors hitting a teacher page get the tutor sign-in view
      if (location !== '/login') navigate(location.startsWith("/teacher") ? "/login?as=tutor" : "/login");
      return;
    }

    if (profile && allowedRoles && !allowedRoles.includes(profile.role)) {
      const dest =
        profile.role === "admin"   ? "/admin" :
        profile.role === "teacher" ? "/teacher/dashboard" :
                                     "/student/dashboard";
      navigate(dest);
    }
  }, [loading, user, profile, location]);

  /* MISSION: PROTECTED ROUTE GATE */
  
  // 1. Loading State -> Show Skeleton
  if (loading) return <DashboardSkeleton />;

  // 3. Not Logged In -> Wait for useEffect to handle redirect
  if (!user) {
    return <DashboardSkeleton />; 
  }

  // 4. Logged In -> Role Validation (Wait for useEffect)
  if (allowedRoles && profile && !allowedRoles.includes(profile.role)) {
    return <DashboardSkeleton />; 
  }

  return <Component />;
}

/* ── Unified Role Redirect ───────────────────────────────────── */
function DashboardRedirect() {
  const { profile, loading } = useAuth();
  
  if (loading) return <DashboardSkeleton />;

  // Memory Check: Restore last visited portal path if it exists
  const lastPath = localStorage.getItem('ib_meridian_last_portal_path');
  if (lastPath) {
    // Only restore if the path matches the user's role
    const isMatched = (profile?.role === 'admin' && lastPath.startsWith('/admin')) ||
                      (profile?.role === 'teacher' && lastPath.startsWith('/teacher')) ||
                      (profile?.role === 'student' && lastPath.startsWith('/student'));
    
    if (isMatched) return <Redirect to={lastPath} />;
  }
  
  const dest = profile?.role === "admin"   ? "/admin" :
               profile?.role === "teacher" ? "/teacher/dashboard" :
                                            "/student/dashboard";
  return <Redirect to={dest} />;
}

/* ── Admin Area Wrapper (Persistent) ────────────────────────── */
function AdminArea() {
  return (
    <AdminLayout>
      <Switch>
        <Route path="/admin">
           <Redirect to="/admin/launch" />
        </Route>
        <Route path="/admin/launch">
           <AdminDashboard />
        </Route>
        <Route path="/admin/analytics">
           <AdminAnalytics />
        </Route>
        <Route path="/admin/resources">
           <AdminResources />
        </Route>
        <Route path="/admin/upload">
           <AdminUploadHub />
        </Route>
        <Route path="/admin/subjects">
           <AdminSubjects />
        </Route>
        {/* User Management */}
        <Route path="/admin/users/students">
           <AdminStudents />
        </Route>
        <Route path="/admin/users/teachers">
           <AdminTeachers />
        </Route>
        <Route path="/admin/tutors">
           <AdminTeachers />
        </Route>
        <Route path="/admin/users/schools">
           <AdminSchools />
        </Route>

        {/* Financials & Logic */}
        <Route path="/admin/revenue">
           <AdminRevenue />
        </Route>
        <Route path="/admin/subscriptions">
           <AdminSubscriptions />
        </Route>
        <Route path="/admin/settings">
           <AdminSettings />
        </Route>
        <Route path="/admin/logs">
           <AdminLogs />
        </Route>
        <Route path="/admin/assignments">
           <AdminAssignments />
        </Route>
        <Route path="/admin/notifications">
           <AdminNotifications />
        </Route>
        <Route path="/admin/testimonials">
           <AdminTestimonials />
        </Route>
        <Route path="/admin/blog">
          <AdminBlog />
        </Route>
        <Route path="/admin/lessons">
          <AdminLessons />
        </Route>
        <Route path="/admin/tutor-applications">
           <AdminTutorApplications />
        </Route>

        {/* Fallback Catch-all for Admin subpaths */}
        {/* Global Fallback for Admin area */}
        <Route path="/admin/:rest*">
           <AdminDashboard />
        </Route>
      </Switch>
    </AdminLayout>
  );
}

function AdminPortal() {
  return <ProtectedRoute allowedRoles={["admin"]} component={AdminArea} />;
}

/* ── Student Area Wrapper (Persistent) ───────────────────────── */
function StudentArea() {
  return (
    <StudentLayout>
      <Switch>
        <Route path="/student">
           <Redirect to="/student/dashboard" />
        </Route>
        <Route path="/student/dashboard">
           <StudentDashboard />
        </Route>
        <Route path="/student/progress">
           <StudentProgress />
        </Route>
        <Route path="/student/resources">
           <StudentResources />
        </Route>
        <Route path="/student/tutors">
           <StudentTutors />
        </Route>
        <Route path="/student/ia-tracker">
           <StudentIaTracker />
        </Route>
        <Route path="/student/countdown">
           <StudentCountdown />
        </Route>
        <Route path="/student/settings">
           <StudentSettings />
        </Route>
        <Route path="/student/portal">
           <StudentDashboard />
        </Route>
        {/* Fallback to dashboard */}
        <Route path="/student/:rest*">
           <StudentDashboard />
        </Route>
      </Switch>
    </StudentLayout>
  );
}

function StudentPortal() {
  return <ProtectedRoute allowedRoles={["student"]} component={StudentArea} />;
}

/* ── Teacher Area Wrapper (Persistent) ───────────────────────── */
function TeacherArea() {
  return (
    <TeacherLayout>
      <Switch>
        <Route path="/teacher">
           <Redirect to="/teacher/dashboard" />
        </Route>
        <Route path="/teacher/dashboard">
           <TeacherDashboard />
        </Route>
        <Route path="/teacher/classes">
           <TeacherClasses />
        </Route>
        <Route path="/teacher/feedback">
           <TeacherFeedback />
        </Route>
        <Route path="/teacher/upload">
           <AdminUploadHub />
        </Route>
        <Route path="/teacher/library">
           <TeacherLibrary />
        </Route>
        <Route path="/teacher/students">
           <TeacherStudents />
        </Route>
        <Route path="/teacher/schedule">
           <TeacherSchedule />
        </Route>
        <Route path="/teacher/settings">
           <TeacherSettings />
        </Route>
        <Route path="/teacher/portal">
           <TeacherDashboard />
        </Route>
        <Route path="/teacher/*">
           <TeacherDashboard />
        </Route>
      </Switch>
    </TeacherLayout>
  );
}

function TeacherPortal() {
  return <ProtectedRoute allowedRoles={["teacher"]} component={TeacherArea} />;
}

function Router() {
  const [location] = useLocation();

  // Navigation Memory: Save last portal path
  useEffect(() => {
    const portalPaths = ['/student/', '/teacher/', '/admin/'];
    if (portalPaths.some(p => location.startsWith(p))) {
      localStorage.setItem('ib_meridian_last_portal_path', location);
    }
  }, [location]);

  return (
    <Suspense fallback={<DashboardSkeleton />}>
      <Switch>
        {/* ── CORE PORTALS (Explicit Root + Subpath matching) ──────────────── */}
        <Route path="/admin"><AdminPortal /></Route>
        <Route path="/admin/*"><AdminPortal /></Route>
        
        <Route path="/teacher"><TeacherPortal /></Route>
        <Route path="/teacher/*"><TeacherPortal /></Route>

        <Route path="/student"><StudentPortal /></Route>
        <Route path="/student/*"><StudentPortal /></Route>

        <Route path="/tutor*"><Redirect to="/teacher/dashboard" /></Route>
        <Route path="/dashboard/submit-testimonial">
          <ProtectedRoute allowedRoles={["student"]} component={SubmitTestimonial} />
        </Route>
        <Route path="/dashboard"><DashboardRedirect /></Route>

        {/* Public Pages */}
        <Route path="/"             component={Landing} />
        <Route path="/auth"         component={AuthPage} />
        <Route path="/login"        component={AuthPage} />
        <Route path="/contact"      component={ContactPage} />
        <Route path="/pricing"      component={PricingPage} />
        <Route path="/checkout"     component={CheckoutPage} />
        <Route path="/resources"    component={ResourcesPage} />
        <Route path="/students"     component={StudentsPage} />
        <Route path="/teachers"     component={TeachersPage} />
        <Route path="/schools"      component={SchoolsPage} />
        <Route path="/tutors"       component={TutorsPage} />
        <Route path="/core"         component={CorePage} />
        <Route path="/students/notes"      component={NotesPage} />
        <Route path="/students/past-papers" component={PastPapersPage} />
        <Route path="/subjects"            component={SubjectsPage} />
        <Route path="/students/study-tools" component={StudyToolsPage} />
        <Route path="/teachers/lesson-plans"         component={LessonPlansPage} />
        <Route path="/teachers/rubrics"              component={RubricsPage} />
        <Route path="/teachers/slide-decks"          component={SlideDecksPage} />
        <Route path="/teachers/ia-lab-support"       component={IaLabSupportPage} />
        <Route path="/teachers/professional-development" component={ProfDevPage} />
        <Route path="/schools/enterprise"              component={EnterprisePage} />
        <Route path="/schools/coordinator"             component={CoordinatorPage} />
        <Route path="/schools/analytics"               component={AnalyticsPage} />
        <Route path="/about"         component={AboutPage} />
        <Route path="/blog"          component={BlogPage} />
        <Route path="/blog/:slug"    component={BlogPostPage} />
        <Route path="/careers"       component={CareersPage} />
        <Route path="/privacy"       component={PrivacyPage} />
        <Route path="/terms"         component={TermsPage} />
        <Route path="/ia"            component={IAPage} />
        <Route path="/testimonials"   component={TestimonialsPage} />
        <Route path="/become-a-tutor" component={BecomeTutorPage} />

        {/* Catch-all global 404 */}
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

/** `ssrPath` is only passed by the build-time prerenderer (src/entry-server.tsx). */
function App({ ssrPath }: { ssrPath?: string }) {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <CartProvider>
            <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")} ssrPath={ssrPath}>
              <TitleHandler />
              <ScrollToTop />
              <Router />
              <CartDrawer />
              <WhatsAppFAB />
            </WouterRouter>
            <Toaster />
            <SonnerToaster position="top-right" expand={false} richColors />
          </CartProvider>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
